import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Compass,
  Loader2,
  MapPin,
  Wallet,
  Backpack,
  Lightbulb,
  AlertTriangle,
  RefreshCcw,
  Route as RouteIcon,
  Sparkles,
  Share2,
  Printer,
} from "lucide-react";
import { PageHeader } from "./fordon";

const URL_SELF = "https://www.akhusbilfritid.se/husbil-resa";

// ---------------------------------------------------------------
// "Bygg din husbil-resa" — läsaren gör sina val och får en unik,
// AI-skriven reseberättelse med dagsetapper, budget och packlista.
// Genereras i realtid, sparas inte, indexeras inte som innehåll.
// ---------------------------------------------------------------

interface TripDay {
  day?: number;
  route?: string;
  km?: number;
  driveTime?: string;
  story?: string;
  stay?: string;
  insiderTip?: string;
}

interface Trip {
  title?: string;
  subtitle?: string;
  intro?: string;
  totalKm?: number;
  days?: TripDay[];
  budget?: {
    fuel?: string;
    camping?: string;
    food?: string;
    extras?: string;
    total?: string;
  };
  packing?: string[];
  vehicleAdvice?: string;
  warnings?: string[];
}

const STARTS = [
  "Kalix, Norrbotten",
  "Luleå",
  "Umeå",
  "Sundsvall",
  "Stockholm",
  "Göteborg",
  "Malmö",
  "Tornio, Finland",
];

const REGIONS = [
  { value: "Norrbotten och Lappland", label: "Norrbotten & Lappland", hint: "Fjäll, älvar, midnattssol" },
  { value: "Norrlandskusten och Höga kusten", label: "Norrlandskusten", hint: "Kust, hamnar, Höga kusten" },
  { value: "Nordnorge via E10 och Lofoten", label: "Nordnorge & Lofoten", hint: "Dramatisk kust, färjor" },
  { value: "finska Lappland och Torneälvdalen", label: "Finska Lappland", hint: "Tornedalen, Rovaniemi" },
  { value: "Dalarna, Bergslagen och Siljansbygden", label: "Dalarna & Siljan", hint: "Sjöar, byar, kultur" },
  { value: "Västkusten från Göteborg till Strömstad", label: "Västkusten", hint: "Bohuslän, skaldjur" },
  { value: "Öland, Gotland och Smålandskusten", label: "Öland & Gotland", hint: "Sol, färja, badvikar" },
];

const SEASONS = [
  { value: "vinter (december–mars)", label: "Vinter", hint: "Norrsken, kyla, eldrift" },
  { value: "vår (april–maj)", label: "Vår", hint: "Tomma platser, snösmältning" },
  { value: "sommar (juni–augusti)", label: "Sommar", hint: "Midnattssol, högsäsong" },
  { value: "höst (september–oktober)", label: "Höst", hint: "Höstfärger, lugn" },
];

const BUDGETS = [
  { value: "snål – ställplatser, egen matlagning", label: "Snålt", hint: "Ställplats & egen mat" },
  { value: "normal – mix av camping och ställplats", label: "Normalt", hint: "Mix camping/ställplats" },
  { value: "generös – camping med full service och restaurangbesök", label: "Generöst", hint: "Full service & restaurang" },
];

const TRAVELLERS = [
  { value: "två personer", label: "Två vuxna" },
  { value: "familj med två barn", label: "Familj med barn" },
  { value: "en person som reser solo", label: "Solo" },
  { value: "två par som reser tillsammans i två fordon", label: "Två fordon" },
  { value: "två personer med hund", label: "Med hund" },
];

const VEHICLES = [
  { value: "halvintegrerad husbil, ca 7 meter", label: "Husbil 7 m" },
  { value: "integrerad husbil, ca 8 meter", label: "Husbil 8 m" },
  { value: "husbil med B-körkort under 3,5 ton", label: "B-körkort 3,5 t" },
  { value: "husvagn med dragbil", label: "Husvagn" },
  { value: "kompakt campervan", label: "Campervan" },
];

const VIBES = [
  { value: "natur, vandring och tystnad", label: "Natur & tystnad" },
  { value: "fiske och älvliv", label: "Fiske" },
  { value: "mat, bagerier och lokala råvaror", label: "Mat & lokalt" },
  { value: "bad, stränder och sol", label: "Bad & sol" },
  { value: "kultur, museer och små byar", label: "Kultur & byar" },
  { value: "långa körsträckor och landskap genom rutan", label: "Roadtrip" },
];

function Chip({
  active,
  onClick,
  children,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-2xl border px-4 py-3 text-left transition-all ${
        active
          ? "border-foreground bg-foreground text-background shadow-lg"
          : "border-border bg-card hover:border-foreground/40 hover:shadow-sm"
      }`}
    >
      <span className="block text-sm font-medium leading-tight">{children}</span>
      {hint && (
        <span
          className={`mt-1 block text-xs leading-snug ${
            active ? "text-background/70" : "text-muted-foreground"
          }`}
        >
          {hint}
        </span>
      )}
    </button>
  );
}

function Field({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-8 first:mt-0">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="font-display text-lg tracking-tight md:text-xl">{label}</h2>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function RouteComponent() {
  const [start, setStart] = useState(STARTS[0]);
  const [region, setRegion] = useState(REGIONS[0].value);
  const [days, setDays] = useState(5);
  const [season, setSeason] = useState(SEASONS[2].value);
  const [budget, setBudget] = useState(BUDGETS[1].value);
  const [travellers, setTravellers] = useState(TRAVELLERS[0].value);
  const [vehicle, setVehicle] = useState(VEHICLES[0].value);
  const [vibe, setVibe] = useState(VIBES[0].value);
  const [extra, setExtra] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);

  const totalKm = useMemo(() => {
    if (!trip?.days?.length) return trip?.totalKm ?? 0;
    const sum = trip.days.reduce((acc, d) => acc + (Number(d.km) || 0), 0);
    return sum || (trip.totalKm ?? 0);
  }, [trip]);

  async function generate() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/resa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start, region, days, season, budget, travellers, vehicle, vibe, extra }),
      });
      if (!res.ok) {
        const text = await res.text();
        setError(text || "Något gick fel. Försök igen.");
        setTrip(null);
        return;
      }
      const data = (await res.json()) as Trip;
      setTrip(data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch {
      setError("Kunde inte nå reseplaneraren. Kontrollera uppkopplingen och försök igen.");
      setTrip(null);
    } finally {
      setLoading(false);
    }
  }

  function share() {
    const text = trip?.title ? `${trip.title} – byggd med AK Husbil & Fritids reseplanerare` : "Bygg din husbil-resa";
    if (typeof navigator !== "undefined" && navigator.share) {
      void navigator.share({ title: text, url: URL_SELF });
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(URL_SELF);
    }
  }

  return (
    <>
      <PageHeader
        label="Reseplaneraren"
        title={<>Bygg din husbil&#8209;resa</>}
        sub="Välj startpunkt, riktning, säsong och känsla — och få en helt egen reseberättelse med dagsetapper, budget, packlista och ärliga varningar. Skriven i realtid, aldrig samma två gånger."
      />

      <section className="container-edge py-14 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <Field label="Var startar resan?" icon={<MapPin className="h-5 w-5 text-muted-foreground" />}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {STARTS.map((s) => (
                  <Chip key={s} active={start === s} onClick={() => setStart(s)}>
                    {s}
                  </Chip>
                ))}
              </div>
            </Field>

            <Field label="Vart är du på väg?" icon={<Compass className="h-5 w-5 text-muted-foreground" />}>
              <div className="grid gap-3 sm:grid-cols-2">
                {REGIONS.map((r) => (
                  <Chip key={r.value} active={region === r.value} onClick={() => setRegion(r.value)} hint={r.hint}>
                    {r.label}
                  </Chip>
                ))}
              </div>
            </Field>

            <Field label="Hur många dagar?" icon={<RouteIcon className="h-5 w-5 text-muted-foreground" />}>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={2}
                  max={14}
                  step={1}
                  value={days}
                  aria-label="Antal dagar"
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="h-2 w-full max-w-md cursor-pointer appearance-none rounded-full bg-muted accent-foreground"
                />
                <span className="min-w-[5.5rem] font-display text-2xl tabular-nums">{days} dagar</span>
              </div>
            </Field>

            <Field label="Säsong">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {SEASONS.map((s) => (
                  <Chip key={s.value} active={season === s.value} onClick={() => setSeason(s.value)} hint={s.hint}>
                    {s.label}
                  </Chip>
                ))}
              </div>
            </Field>

            <Field label="Budgetnivå" icon={<Wallet className="h-5 w-5 text-muted-foreground" />}>
              <div className="grid gap-3 sm:grid-cols-3">
                {BUDGETS.map((b) => (
                  <Chip key={b.value} active={budget === b.value} onClick={() => setBudget(b.value)} hint={b.hint}>
                    {b.label}
                  </Chip>
                ))}
              </div>
            </Field>

            <Field label="Vilka reser?">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {TRAVELLERS.map((t) => (
                  <Chip key={t.value} active={travellers === t.value} onClick={() => setTravellers(t.value)}>
                    {t.label}
                  </Chip>
                ))}
              </div>
            </Field>

            <Field label="Fordon">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {VEHICLES.map((v) => (
                  <Chip key={v.value} active={vehicle === v.value} onClick={() => setVehicle(v.value)}>
                    {v.label}
                  </Chip>
                ))}
              </div>
            </Field>

            <Field label="Känsla och fokus">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {VIBES.map((v) => (
                  <Chip key={v.value} active={vibe === v.value} onClick={() => setVibe(v.value)}>
                    {v.label}
                  </Chip>
                ))}
              </div>
            </Field>

            <Field label="Något speciellt du vill få med? (frivilligt)">
              <textarea
                value={extra}
                onChange={(e) => setExtra(e.target.value.slice(0, 400))}
                rows={3}
                placeholder="T.ex. vi vill fiska en dag, undvika färjor, ha kort körning per dag, hitta hundvänliga platser…"
                className="w-full rounded-2xl border border-border bg-card p-4 text-sm leading-relaxed outline-none focus:border-foreground/50"
              />
              <p className="mt-2 text-xs text-muted-foreground">{extra.length}/400 tecken</p>
            </Field>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={generate}
                disabled={loading}
                className="btn-cta btn-cta-dark inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-medium disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Skriver din resa…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Bygg min resa
                  </>
                )}
              </button>
              {trip && !loading && (
                <button
                  type="button"
                  onClick={generate}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-4 text-sm hover:border-foreground/40"
                >
                  <RefreshCcw className="h-4 w-4" />
                  Skriv en ny variant
                </button>
              )}
            </div>
            {loading && (
              <p className="mt-4 max-w-lg text-sm text-muted-foreground">
                Planeraren räknar etapper, avstånd, bränsle och boende. Det tar oftast 15–40 sekunder — längre resor tar längre tid.
              </p>
            )}
            {error && (
              <div className="mt-6 flex max-w-xl items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-3xl border border-border bg-warm p-6">
              <p className="eyebrow">Så funkar det</p>
              <ol className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
                <li>1. Välj start, riktning, dagar och säsong.</li>
                <li>2. Vi skriver en unik resa med etapper, boende och budget.</li>
                <li>3. Justera valen och kör igen — varje resa blir ny.</li>
              </ol>
              <div className="mt-6 border-t border-border pt-6">
                <p className="text-sm leading-relaxed">
                  Vet du inte vilket fordon som passar rutten? Kör vår matchning mot vårt riktiga lager.
                </p>
                <Link
                  to="/husbil-matchning"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium underline decoration-1 underline-offset-4"
                >
                  Husbil-matchning i 6 frågor
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="mt-6 border-t border-border pt-6">
                <Link
                  to="/fordon"
                  className="inline-flex items-center gap-2 text-sm font-medium underline decoration-1 underline-offset-4"
                >
                  Se fordon i lager
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {trip && (
        <section ref={resultRef} className="border-t border-border bg-warm py-14 md:py-20">
          <div className="container-edge">
            <div className="max-w-3xl">
              <p className="eyebrow">Din resa</p>
              <h2 className="mt-4 font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
                {trip.title || "Din husbilsresa"}
              </h2>
              {trip.subtitle && (
                <p className="mt-5 text-lg leading-relaxed text-muted-foreground md:text-xl">{trip.subtitle}</p>
              )}
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                <span className="rounded-full border border-border bg-card px-4 py-2">{days} dagar</span>
                {totalKm > 0 && (
                  <span className="rounded-full border border-border bg-card px-4 py-2">
                    ca {totalKm.toLocaleString("sv-SE")} km
                  </span>
                )}
                <span className="rounded-full border border-border bg-card px-4 py-2">{start}</span>
              </div>
              {trip.intro && <p className="mt-8 text-base leading-relaxed md:text-lg">{trip.intro}</p>}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={share}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm hover:border-foreground/40"
                >
                  <Share2 className="h-4 w-4" />
                  Dela
                </button>
                <button
                  type="button"
                  onClick={() => typeof window !== "undefined" && window.print()}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm hover:border-foreground/40"
                >
                  <Printer className="h-4 w-4" />
                  Skriv ut / spara PDF
                </button>
              </div>
            </div>

            <div className="mt-12 space-y-6">
              {trip.days?.map((d, i) => (
                <article key={i} className="rounded-3xl border border-border bg-card p-6 md:p-8">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <span className="font-display text-3xl tabular-nums md:text-4xl">
                      Dag {d.day ?? i + 1}
                    </span>
                    {d.route && <span className="text-base font-medium md:text-lg">{d.route}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {d.km ? <span>{Number(d.km).toLocaleString("sv-SE")} km</span> : null}
                    {d.driveTime && <span>{d.driveTime} bakom ratten</span>}
                  </div>
                  {d.story && <p className="mt-5 text-base leading-relaxed">{d.story}</p>}
                  {d.stay && (
                    <p className="mt-5 flex gap-3 text-sm leading-relaxed">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>
                        <strong className="font-medium">Övernattning: </strong>
                        {d.stay}
                      </span>
                    </p>
                  )}
                  {d.insiderTip && (
                    <p className="mt-4 flex gap-3 rounded-2xl bg-warm p-4 text-sm leading-relaxed">
                      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>
                        <strong className="font-medium">Proffstips: </strong>
                        {d.insiderTip}
                      </span>
                    </p>
                  )}
                </article>
              ))}
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-2">
              {trip.budget && (
                <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-display text-2xl tracking-tight">Vad kostar det?</h3>
                  </div>
                  <dl className="mt-5 space-y-3 text-sm">
                    {[
                      ["Bränsle", trip.budget.fuel],
                      ["Boende", trip.budget.camping],
                      ["Mat", trip.budget.food],
                      ["Övrigt", trip.budget.extras],
                    ]
                      .filter(([, v]) => Boolean(v))
                      .map(([k, v]) => (
                        <div key={k as string} className="flex flex-wrap justify-between gap-2 border-b border-border pb-3">
                          <dt className="text-muted-foreground">{k}</dt>
                          <dd className="text-right font-medium">{v}</dd>
                        </div>
                      ))}
                    {trip.budget.total && (
                      <div className="flex flex-wrap justify-between gap-2 pt-1">
                        <dt className="font-medium">Totalt</dt>
                        <dd className="text-right font-display text-xl">{trip.budget.total}</dd>
                      </div>
                    )}
                  </dl>
                  <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
                    Uppskattning för svensk prisnivå 2026. Faktiska kostnader varierar med förbrukning, säsong och val av plats.
                  </p>
                </div>
              )}

              {trip.packing?.length ? (
                <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
                  <div className="flex items-center gap-2">
                    <Backpack className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-display text-2xl tracking-tight">Packlista för just den här resan</h3>
                  </div>
                  <ul className="mt-5 space-y-2.5 text-sm leading-relaxed">
                    {trip.packing.map((p, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/40" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            {(trip.vehicleAdvice || trip.warnings?.length) && (
              <div className="mt-6 grid gap-6 lg:grid-cols-2">
                {trip.vehicleAdvice && (
                  <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
                    <h3 className="font-display text-2xl tracking-tight">Vilket fordon passar rutten?</h3>
                    <p className="mt-4 text-sm leading-relaxed md:text-base">{trip.vehicleAdvice}</p>
                    <Link
                      to="/fordon"
                      className="mt-6 inline-flex items-center gap-2 text-sm font-medium underline decoration-1 underline-offset-4"
                    >
                      Se vilka fordon vi har inne nu
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}
                {trip.warnings?.length ? (
                  <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                      <h3 className="font-display text-2xl tracking-tight">Ärliga varningar</h3>
                    </div>
                    <ul className="mt-5 space-y-2.5 text-sm leading-relaxed">
                      {trip.warnings.map((w, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/40" />
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}

            <div className="mt-12 rounded-3xl border border-border bg-card p-6 md:p-8">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Reseförslaget är automatiskt genererat utifrån dina val och ska läsas som inspiration — kontrollera
                alltid öppettider, färjetider, vägläge och bokningsläge innan du kör. Vill du ha hjälp att planera på
                riktigt, eller hitta fordonet som klarar rutten?{" "}
                <Link to="/kontakt" className="font-medium underline decoration-1 underline-offset-4">
                  Ring eller mejla oss
                </Link>{" "}
                — vi har kört de flesta av dessa vägar själva.
              </p>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export const Route = createFileRoute("/husbil-resa")({
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: buildTitle("Bygg din husbilsresa – gratis reseplanerare") },
      {
        name: "description",
        content:
          "Välj startpunkt, riktning, säsong och budget – få en unik husbilsresa med dagsetapper, körtider, boende och packlista. Gratis reseplanerare.",
      },
      { property: "og:title", content: "Bygg din husbil-resa – interaktiv reseplanerare" },
      {
        property: "og:description",
        content:
          "En helt egen reseberättelse med etapper, budget och packlista – byggd på dina val. Aldrig samma resa två gånger.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL_SELF },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Bygg din husbil-resa",
          applicationCategory: "TravelApplication",
          operatingSystem: "Web",
          url: URL_SELF,
          description:
            "Interaktiv reseplanerare för husbil och husvagn. Ger dagsetapper, körtider, boendetyper, budget och packlista utifrån startpunkt, säsong och budgetnivå.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "SEK" },
          provider: {
            "@type": "Organization",
            name: "AK Husbil & Fritid",
            url: "https://www.akhusbilfritid.se",
          },
        }),
      },
    ],
  }),
});
